import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { images } from '../utils/images';
import { Users, Target, Award, Heart, UserCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';

const AboutPage = () => {
  const teamMembers = [
    {
      name: "Kristian Hartung",
      role: "Founder",
      bio: "Kristian is the heart behind Life Care and Wellness. After 15 years as a senior teacher working with children and young people, he moved into the disability sector because he saw first-hand how many people were falling through the cracks. With a background in business, mental health qualifications, and experience as a registered counsellor in forensic and juvenile justice settings, Kristian brings both professional expertise and genuine human compassion to everything we do.",
      icon: true
    },
    {
      name: "Terri Hartung",
      role: "Registered Counsellor & Teacher",
      bio: "Terri is a registered trauma-informed counsellor and teacher with 30 years of experience across counselling, education, child protection, and creative arts. She works with children, adults, parents, and carers living with neurodiversity, trauma, and complex PTSD. In simple terms — Terri helps people who've been through tough times find their strength again and build a life that feels good.",
      icon: true
    },
    {
      name: "Sharyn Kerr",
      role: "Registered Nurse",
      bio: "Sharyn is a highly experienced registered nurse with over 25 years in nursing and disability care. What makes Sharyn special isn't just her clinical knowledge — it's her ability to truly connect with people on a human level. She brings both professional expertise and personal insight into mental health, and she treats every person she works with the way she'd want her own family to be treated.",
      icon: true
    }
  ];

  const values = [
    {
      icon: Heart,
      title: "We See You as a Person First",
      description: "Too many providers treat people as case numbers. We don't. We take the time to understand who you are — your story, your struggles, your dreams. Everything we do starts with genuinely caring about you as a human being, not just a participant."
    },
    {
      icon: Award,
      title: "We Know What We're Doing",
      description: "Our team has decades of combined experience in disability support, mental health, counselling, and nursing. But more importantly, we stay curious and keep learning. We don't rely on outdated methods — we use what actually works, explained in ways you can understand."
    },
    {
      icon: Target,
      title: "We Build Plans Around Your Life",
      description: "Our services aren't one-size-fits-all. From support coordination to counselling, employment support to community access, we tailor everything to fit your unique situation. We also offer free consultations and a free action plan based on your current NDIS plan — because getting help shouldn't cost you before it even starts."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="About Us" description="Learn about Life Care and Wellness — a team of real people who genuinely care about your wellbeing, offering NDIS disability support services in Brisbane and Logan." />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Real People. Genuine Care. That's Who We Are.
              </h1>
              <p 
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                Life Care and Wellness was built on a simple belief: everyone deserves to be treated with dignity, respect, and real human warmth. We support people in reaching their full potential through personalised, compassionate care — the kind that makes you feel like you matter, because you do.
              </p>
              <p 
                className="text-lg leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                Our team offers services across daily living, community access, school leaver support, counselling, independent living, and therapeutic music. We also provide multilingual support — because clear, inclusive communication is something everyone deserves.
              </p>
              <p 
                className="text-base leading-relaxed italic"
                style={{ color: '#6c935e' }}
              >
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={images.about}
                alt="Life Care and Wellness team"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={images.whyChoose}
                  alt="Caring NDIS consultation"
                  className="w-full h-[350px] object-cover"
                />
              </div>
              <div>
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ color: '#587740' }}
                >
                  Our Promise to You
                </h2>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  Our team includes nurses, counsellors, support workers, trainers, and coordinators — all with deep knowledge and, more importantly, genuine empathy. We also help with NDIS consulting and business support, whether you're starting a service or preparing for registration.
                </p>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  We create tailored plans based on each person's goals — put simply, we match you with the right support worker, build a plan that fits your life, and walk beside you every step of the way.
                </p>
                <div 
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: '#81b95610' }}
                >
                  <p className="text-base font-semibold" style={{ color: '#587740' }}>
                    We offer free consultations and a free action plan based on your current NDIS plan. No commitment, no cost — just honest guidance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center"
            style={{ color: '#587740' }}
          >
            What We Stand For
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card 
                  key={index}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-8">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: '#AABC5A' }}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 
                      className="text-2xl font-bold mb-4"
                      style={{ color: '#587740' }}
                    >
                      {value.title}
                    </h3>
                    <p 
                      className="text-base leading-relaxed"
                      style={{ color: '#5e5e5e' }}
                    >
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="flex items-center justify-center mb-6">
              <Users 
                className="w-12 h-12 mr-4"
                style={{ color: '#81b956' }}
              />
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold"
                style={{ color: '#587740' }}
              >
                The People Behind the Care
              </h2>
            </div>
            <p 
              className="text-lg md:text-xl"
              style={{ color: '#5e5e5e' }}
            >
              We're not a faceless corporation. We're real people with real experience, and we genuinely care about the people we work with. Here's who you'll be trusting with your support.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <Card 
                key={index}
                className="border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div 
                  className="h-64 flex items-center justify-center"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <UserCircle 
                    className="w-32 h-32"
                    style={{ color: '#81b956' }}
                  />
                </div>
                <CardContent className="p-8">
                  <h3 
                    className="text-2xl font-bold mb-2"
                    style={{ color: '#587740' }}
                  >
                    {member.name}
                  </h3>
                  <p 
                    className="text-lg font-semibold mb-4"
                    style={{ color: '#81b956' }}
                  >
                    {member.role}
                  </p>
                  <p 
                    className="text-base leading-relaxed"
                    style={{ color: '#5e5e5e' }}
                  >
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Ready to Be Treated the Way You Deserve?
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Book a free consultation with us. We'll sit down, listen to your story, review your situation, and create a personalised action plan — all at no cost. Because getting the right support should never depend on your wallet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button
                  className="px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
                >
                  Get Your Free Consultation
                </button>
              </a>
              <a href="/get-on-ndis-free">
                <button
                  className="px-8 py-4 rounded-lg text-lg font-semibold border-2 border-white text-white hover:bg-white/10 transition-colors"
                >
                  Get on the NDIS Free
                </button>
              </a>
            </div>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default AboutPage;
